'use client'

import { useEffect } from 'react'
import type { Address } from 'viem'
import { useStore } from '@/domain/state'
import type { UserCollateralState } from './types'

interface UseCleanupClosedPositionNamesParams {
  owner: Address | undefined
  state: UserCollateralState | null
  isLoading: boolean
}

/**
 * Clears custom names for subaccounts that no longer have any collateral or debt.
 * Reuses the same collateral state that powers the positions UI.
 */
export function useCleanupClosedPositionNames({
  owner,
  state,
  isLoading,
}: UseCleanupClosedPositionNamesParams): void {
  const names = useStore((store) => store.positionNames.names)
  const clearPositionName = useStore((store) => store.positionNames.clearPositionName)

  useEffect(() => {
    if (!owner || !state || isLoading) return

    const activeIds = new Set<number>()
    for (const collateral of state.collaterals) {
      activeIds.add(collateral.subAccountId)
    }
    for (const borrow of state.borrows) {
      activeIds.add(borrow.subAccountId)
    }

    const ownerPrefix = `${owner.toLowerCase()}:`
    for (const key of Object.keys(names)) {
      if (!key.startsWith(ownerPrefix)) continue

      const id = Number(key.slice(ownerPrefix.length))
      if (Number.isInteger(id) && id >= 0 && id <= 255 && !activeIds.has(id)) {
        clearPositionName(owner, id)
      }
    }
  }, [owner, state, isLoading, names, clearPositionName])
}
